import { useSyncExternalStore } from "react";

/**
 * Single source of truth for the color theme: the `data-theme` attribute on
 * <html>. Light is the default regardless of the OS preference; dark only
 * when the user picks it. The choice is persisted in localStorage and applied
 * before first paint by the shell's /theme-init.js (which uses the same key).
 */
export type Theme = "light" | "dark";

export const THEME_STORAGE_KEY = "nabble.theme";

type Listener = () => void;
const listeners = new Set<Listener>();

function readStored(): Theme | null {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return value === "dark" || value === "light" ? value : null;
  } catch {
    return null;
  }
}

function writeStored(theme: Theme): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Storage can be unavailable (private mode, blocked site data): the
    // theme still applies for this page, it just won't persist.
  }
}

function applyToDocument(theme: Theme): void {
  const root = document.documentElement;
  if (root.getAttribute("data-theme") === theme) return;
  // Switch without animating every color transition on the page.
  root.classList.add("nb-theme-switching");
  root.setAttribute("data-theme", theme);
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => root.classList.remove("nb-theme-switching"));
  });
}

function emit(): void {
  for (const listener of listeners) listener();
}

/** The theme currently rendered (what `data-theme` says). */
export function getTheme(): Theme {
  if (typeof document === "undefined") return "light";
  return document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";
}

export function setTheme(theme: Theme): void {
  applyToDocument(theme);
  writeStored(theme);
  emit();
}

export function toggleTheme(): Theme {
  const next: Theme = getTheme() === "dark" ? "light" : "dark";
  setTheme(next);
  return next;
}

/**
 * Makes sure <html> carries the stored theme (light when nothing is stored).
 * Idempotent; the pre-paint script normally did this already.
 */
export function initTheme(): Theme {
  const theme = readStored() ?? "light";
  applyToDocument(theme);
  return theme;
}

function onStorage(event: StorageEvent): void {
  if (event.key !== THEME_STORAGE_KEY) return;
  const value = event.newValue === "dark" ? "dark" : "light";
  applyToDocument(value);
  emit();
}

export function subscribeTheme(listener: Listener): () => void {
  if (listeners.size === 0 && typeof window !== "undefined") {
    window.addEventListener("storage", onStorage);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && typeof window !== "undefined") {
      window.removeEventListener("storage", onStorage);
    }
  };
}

/** React binding: re-renders whenever the rendered theme changes (this tab or another). */
export function useTheme(): { theme: Theme; setTheme: (theme: Theme) => void; toggleTheme: () => Theme } {
  const theme = useSyncExternalStore(subscribeTheme, getTheme, () => "light" as Theme);
  return { theme, setTheme, toggleTheme };
}
